import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useDashboardContext } from "../../contexts/DashboardContext";
import LinearProgress from '@mui/material/LinearProgress';
import DataGridTable from "./DataGridTable";
import { styled } from "@mui/material/styles";
import OneTapButton from "./OneTapButton";
import { useNavigate } from "react-router-dom";
import { Stack } from "@mui/material";
import { getAllTrialsByPatientId } from "../../utils/fastAPI";
import ReportGenerator from "./ReportGenerator";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const PatientHomePage = () => {
  const { patientId, patientName, patientInfo } = useDashboardContext();
  const { allTrials, latestTrialId, isLoading } = useDashboardContext();
  const { setAllTrials, setLatestTrial, setLatestTrialId, setIsLoading } = useDashboardContext();
  const [openReportModal, setOpenReportModal] = useState(false);

  // Get complete patient name from either patientName or patientInfo
  const fullPatientName = React.useMemo(() => {
    if (patientName && patientName.trim() !== '' && patientName !== 'undefined') {
      return patientName;
    }
    
    if (patientInfo) {
      if (patientInfo.firstName && patientInfo.lastName) {
        return `${patientInfo.firstName} ${patientInfo.lastName}`;
      }
      if (patientInfo.name) {
        return patientInfo.name;
      }
    }
    
    return `Patient ${patientId}`;
  }, [patientName, patientInfo, patientId]);

  const handleRefresh = async () => {
    if (patientId) {
      setIsLoading(true);
      const trials = await getAllTrialsByPatientId(patientId);
      if (trials) {
        const latestTrial = trials[0];
        const latestTrialId = trials[0]?.id;
        setAllTrials(trials);
        setLatestTrial(latestTrial);
        setLatestTrialId(latestTrialId);
        setIsLoading(false);
      }
    }
  }

  const StyledTypography = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#5a5a5a',
    display: 'inline-block',
    marginRight: theme.spacing(2),
  }));

  const navigate = useNavigate();

  const handleAnalyzeClick = () => {
    navigate(`/dashboard/${patientId}/analyze/${latestTrialId}`);
  }

  const handleCompareClick = () => {
    navigate(`/dashboard/${patientId}/compare/${latestTrialId}/vs/${latestTrialId}`);
  }
  
  const handleCreateReportClick = () => {
    setOpenReportModal(true);
  }

  // Custom version of OneTapButton for the Report button
  const EnhancedReportButton = (props) => {
    const [isHovered, setIsHovered] = useState(false);

    const enhancedButtonStyle = {
      flexGrow: 0,
      flexBasis: 'calc(33.333% - 16px)',
      maxWidth: 'calc(33.333% - 16px)',
      margin: '8px',
      background: 'linear-gradient(to right bottom, #ffffff, #f0f7ff)',
      border: '2px solid #63C7CF',
      borderRadius: '8px',
      padding: '10px 20px',
      fontSize: '17px',
      cursor: 'pointer',
      transition: 'all 0.3s',
      textAlign: 'left',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'center',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
      position: 'relative',
      overflow: 'hidden',
    };

    const baseTextStyle = {
      margin: 1,
      padding: 0,
    };

    const firstLineStyle = {
      ...baseTextStyle,
      fontWeight: 'bold',
    };

    const secondLineStyle = {
      ...baseTextStyle,
      fontWeight: 'normal',
      fontSize: '14px',
    };

    const hoverStyle = {
      ...enhancedButtonStyle,
      background: 'linear-gradient(to right bottom, #f0f7ff, #e1f0ff)',
      boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)'
    };

    // Shimmer effect style
    const shimmerStyle = {
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '50%',
      height: '100%',
      background: 'linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.4) 50%, rgba(255,255,255,0) 100%)',
      transform: isHovered ? 'translateX(300%)' : 'translateX(0%)',
      transition: 'transform 1s ease-in-out',
      pointerEvents: 'none'
    };

    return (
      <button
        style={isHovered ? hoverStyle : enhancedButtonStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={props.onClick}
      >
        <div style={shimmerStyle}></div>
        <span style={firstLineStyle}>
          {props.title}
        </span>
        <span style={secondLineStyle}>{props.subtitle}</span>
      </button>
    );
  };

  return (
    <Box display="flex" flexDirection="column" m={0.5}>
      {/* Subtitle */}
      {/* <StyledTypography variant="h6">
        One Tap Functions
      </StyledTypography> */}
      {/* One Tap Button */}
      <Box mb={1}>
        <Box display="flex">
          <EnhancedReportButton
            title={
              <Box display="flex" alignItems="center">
                Create new session report
                {/* <AutoAwesomeIcon sx={{ ml: 1, fontSize: 17 }} /> */}
              </Box>
            } 
            subtitle={`Generate a report for ${fullPatientName}`} 
            onClick={handleCreateReportClick} 
          />
          <OneTapButton title="Go to latest clinical trial" subtitle="Detail data and trend chart" onClick={handleAnalyzeClick} />
          <OneTapButton title="Compare two trials" subtitle="Detail data and trend chart" onClick={handleCompareClick} />
        </Box>
      </Box>
      <Box>
        <Box mb={1}>
          <StyledTypography variant="h6" >
            All Trials
          </StyledTypography>
        </Box>
        {isLoading ? (
          <Stack direction="row" justifyContent="center" mt={15}>
            <Box width="100%">
              <LinearProgress />
            </Box>
          </Stack>
        ) : <DataGridTable data={allTrials} handleRefresh={handleRefresh} />
        }
      </Box>
      
      {/* Report Generator Modal */}
      <ReportGenerator 
        open={openReportModal} 
        onClose={() => setOpenReportModal(false)} 
        patientName={fullPatientName}
      />
    </Box >
  );
};

export default PatientHomePage;
